import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, CreditCard, QrCode, Banknote, Loader2 } from "lucide-react";
import { api, brl, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const COUPONS = { TIME10: 0.1, MAGE15: 0.15 };
const SERVICE_FEE = 0.99;
const PAYMENTS = [
  { id: "cartao", label: "Cartão de crédito", icon: CreditCard, hint: "Visa •••• 4291" },
  { id: "pix", label: "Pix", icon: QrCode, hint: "Aprovação imediata" },
  { id: "dinheiro", label: "Dinheiro", icon: Banknote, hint: "Pague na entrega" },
];

export default function Checkout() {
  const { cart, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const saved = user?.addresses?.[0]?.value || "";
  const [address, setAddress] = useState(saved);
  const [payment, setPayment] = useState("pix");
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(false);
  const applied = localStorage.getItem("gf_coupon") || "";

  useEffect(() => {
    if (!cart.store && !placed) navigate("/", { replace: true });
  }, [cart.store, placed, navigate]);

  const deliveryFee = cart.store?.delivery_fee || 0;
  const discount = applied ? subtotal * (COUPONS[applied] || 0) : 0;
  const total = subtotal - discount + deliveryFee + SERVICE_FEE;

  const submit = async () => {
    if (!address.trim()) return toast.error("Informe o endereço de entrega.");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        store_id: cart.store.id,
        items: cart.items.map((i) => ({ item_id: i.id, qty: i.qty })),
        address,
        payment_method: payment,
        coupon: applied || null,
      });
      setPlaced(true);
      localStorage.removeItem("gf_coupon");
      toast.success("Pedido enviado para a loja!");
      navigate(`/pedido/${data.id}`, { replace: true });
      clear();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!cart.store) return null;

  return (
    <div data-testid="checkout-page" className="pb-32">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1 gf-press" data-testid="checkout-back-button">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display font-extrabold text-lg">Finalizar pedido</h1>
      </header>

      <section className="px-4 pt-5">
        <h2 className="font-display font-bold flex items-center gap-2 text-base">
          <MapPin size={17} className="text-gf-orange" /> Endereço de entrega
        </h2>
        <Input
          data-testid="checkout-address-input"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Rua, número, bairro"
          className="mt-2 h-12 rounded-xl"
        />
        <div className="flex gap-2 mt-2 flex-wrap">
          {(user?.addresses || []).map((a) => (
            <button
              key={a.label}
              data-testid={`address-chip-${a.label}`}
              onClick={() => setAddress(a.value)}
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-gf-gray hover:bg-orange-50 gf-press"
            >
              {a.label}
            </button>
          ))}
        </div>
      </section>

      <section className="px-4 mt-6">
        <h2 className="font-display font-bold text-base">Forma de pagamento</h2>
        <div className="grid gap-2 mt-2">
          {PAYMENTS.map((p) => (
            <button
              key={p.id}
              data-testid={`payment-${p.id}`}
              onClick={() => setPayment(p.id)}
              className="flex items-center gap-3 p-3.5 rounded-2xl border-2 bg-white text-left gf-press"
              style={{ borderColor: payment === p.id ? "#FF7A1A" : "#E5E7EB", transition: "border-color .2s ease" }}
            >
              <p.icon size={20} className={payment === p.id ? "text-gf-orange" : "text-gray-400"} />
              <div className="flex-1">
                <p className="font-semibold text-sm">{p.label}</p>
                <p className="text-[11px] text-gray-500">{p.hint}</p>
              </div>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          Pagamento simulado nesta versão de demonstração (sem cobrança real).
        </p>
      </section>

      <section className="px-4 mt-6">
        <h2 className="font-display font-bold text-base">Resumo do pedido</h2>
        <div className="mt-2 rounded-2xl border border-gray-200 p-4 text-sm space-y-1.5">
          <p className="font-semibold text-gf-black">{cart.store.name}</p>
          {cart.items.map((i) => (
            <div key={i.id} className="flex justify-between text-gray-600">
              <span>{i.qty}× {i.name}</span>
              <span>{brl(i.price * i.qty)}</span>
            </div>
          ))}
          <div className="pt-2 border-t border-dashed space-y-1">
            <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{brl(subtotal)}</span></div>
            {applied > "" && (
              <div className="flex justify-between text-mage-green"><span>Desconto ({applied})</span><span>- {brl(discount)}</span></div>
            )}
            <div className="flex justify-between text-gray-600"><span>Taxa de entrega</span><span>{brl(deliveryFee)}</span></div>
            <div className="flex justify-between text-gray-600">
              <span>Taxa de serviço</span><span data-testid="checkout-service-fee">{brl(SERVICE_FEE)}</span>
            </div>
            <div className="flex justify-between font-display font-extrabold text-base pt-1">
              <span>Total</span>
              <span data-testid="checkout-total" className="text-gf-orange">{brl(total)}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="fixed bottom-[76px] left-1/2 -translate-x-1/2 w-[calc(100%-32px)] max-w-[488px] z-40">
        <Button
          data-testid="place-order-button"
          onClick={submit}
          disabled={busy}
          className="w-full py-3.5 h-auto rounded-2xl gf-gradient text-white font-display font-extrabold text-base gf-press shadow-xl"
        >
          {busy ? <Loader2 className="animate-spin" size={18} /> : `Confirmar pedido · ${brl(total)}`}
        </Button>
      </div>
    </div>
  );
}
